import {
  ActionPopUpSingleButton,
  ChapterCard,
  ChipBadge,
  DashboardGrid,
  IStyles,
  Loader,
  NoContentCard,
  SubjectCard,
  deepClone,
  deserify,
  getMediaBasePath,
  pxToRem,
  pxTovW,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { Box, Typography } from '@mui/material';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  NavigateFunction,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useGlobalContext } from '../../../app/Context/GlobalContextProvider';
import { useAppSelector } from '../../../reduxStore/reduxHooks';
import { HOME } from '../../../routeHandling/RoutesNomenclature';
import { getSubjectsMap } from '../../../utils/icons';

import { Feature } from '@protos/school_management/school.db_pb';
import { setSubjectChapterInfo } from '../../Learn/reducer/learn.slice';
import { getModuleReadiness, IPalModuleStatus } from '../api/module';
import { onChapterSelect } from '../utils';

interface IPopupProps {
  iconName: string;
  popupText: string;
  splitText?: string;
  buttontext: string;
  ctaHandler: () => void;
}

const styles: IStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    padding: {
      md: `${pxTovW(50)} ${pxTovW(40)}`,
      lg: `${pxTovW(50)} ${pxTovW(240)}`,
    },
  },
  subjectBox: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    justifyContent: 'space-between',
    padding: { xs: pxToRem(20), md: 0 },
  },
};
export default function PalLearnDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const subjectChapterInfo = deserify(
    useAppSelector((state) => state.learn.subject_chapter_info)
  );
  const { userInfo } = deserify(useAppSelector((state) => state.auth));
  const [popupDetails, setPopupDetails] = useState<IPopupProps>({
    iconName: '',
    buttontext: 'ok',
    popupText: '',
    ctaHandler: () => setActionPopupState(false),
  });
  const [actionPopupState, setActionPopupState] = useState(false);
  const { subject_id } = useParams();
  const subjectId = Number(subject_id);
  const learnSubjects =
    deserify(useAppSelector((state) => state.auth.userInfo?.learnSubjects)) ||
    [];
  const subMap = getSubjectsMap(learnSubjects);
  const user = deserify(useAppSelector((state) => state.auth.userInfo));
  // const { userInfo } = deserify(useAppSelector((state) => state.auth));
  const [loading, setLoading] = useState<'loading' | 'completed' | 'error'>(
    'completed'
  );
  const { setSelectedFunction } = useGlobalContext();
  const commonServiceClientContext = useCommonServiceClientContext();

  const { ContentCommonAPIServiceV1ClientWithStatusCodeHandler } =
    commonServiceClientContext;

  const [externalModuleMapping, setExternalModuleMapping] = useState<
    Record<number, IPalModuleStatus>
  >({});

  const backButtonClick = async () => {
    navigate(HOME);
  };

  useEffect(() => {
    if (subject_id && !isNaN(Number(subject_id))) {
      fetchSubjectChapterInfo();
    }
    setSelectedFunction(() => backButtonClick);

    // (async () => {
    //   const searchedBookId = getBookId(userInfo, subjectChapterInfo?.subjectId);
    //   if (!selected_book_id) dispatch(setHomeSelectedBookId(searchedBookId));
    //   await aiBookOpenEvent(
    //     selected_book_id ? selected_book_id : searchedBookId
    //   );
    // })();

    return () => {
      setSelectedFunction(null);
    };
  }, []);

  const fetchAndSetExternalModuleMapping = async (moduleIds: number[]) => {
    try {
      if (moduleIds.length === 0) return;
      const resp = await getModuleReadiness({ module_ids: moduleIds });
      if (resp?.data) {
        if (Array.isArray(resp.data)) {
          const externalMap = { ...externalModuleMapping };
          resp.data.forEach((elem) => {
            externalMap[Number(elem.moduleId)] = elem;
          });
          setExternalModuleMapping(externalMap);
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  const fetchSubjectChapterInfo = async () => {
    try {
      setLoading('loading');
      const response =
        await ContentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchSubjectChapterInfo(
          {
            personId: user?.studentProfileId,
            personType: ProfileRolesEnum.PROFILE_ROLE_STUDENT,
            subjectId: subjectId,
            bookId: (subjectId && subMap[subjectId]?.bookId) || 0,
            sectionId: user?.classSectionDetails?.sectionId,
          }
        );
      if (response) {
        if (response?.data) {
          dispatch(setSubjectChapterInfo(response.data));
          await fetchAndSetExternalModuleMapping(
            response.data.response.map((elem) => elem.chapterId)
          );
          setLoading('completed');
          return;
        }
      }
      dispatch(setSubjectChapterInfo(undefined));
    } catch (err) {
      setLoading('error');
      console.log(err);
      dispatch(setSubjectChapterInfo(undefined));
    }
  };

  const renderStartResumeButton = (status: 'start' | 'resume' | 'error') => {
    const background =
      status === 'start'
        ? '#D4FFE7'
        : status == 'resume'
        ? '#D6EDFF'
        : '#FFEAEA';
    const border =
      status == 'start'
        ? '#0AA34F'
        : status == 'resume'
        ? '#0760A6'
        : '#F54040';
    const color =
      status == 'start'
        ? '#0AA34F'
        : status == 'resume'
        ? '#0760A6'
        : '#F54040';
    const text =
      status == 'start'
        ? t('PAL_LD_START')
        : status == 'resume'
        ? t('PAL_LD_RESUME')
        : t('PAL_LD_NOT_READY');
    return (
      <Box
        sx={{
          background: `${background}`,
          border: `1px solid ${border}`,
          padding: '3px 5px',
          position: 'absolute',
          bottom: { xs: '50%', md: pxTovW(10) },
          transform: { xs: 'translateY(50%)', md: 'none' },
          right: { xs: pxToRem(12), md: pxTovW(10) },
          borderRadius: '5px',
          display: 'flex',
        }}
      >
        <Typography
          sx={{
            color: `${color}`,
          }}
          variant="smallText"
        >
          {text}
        </Typography>
      </Box>
    );
  };

  const items = subjectChapterInfo?.response.map((elem, index) => {
    const { externalId, isReady, isStarted } =
      externalModuleMapping[elem.chapterId] || {};
    // console.log({
    //   chapterId: elem.chapterId,
    //   mapping: externalModuleMapping[elem.chapterId] || {},
    // });
    return (
      <ChapterCard
        image={getMediaBasePath(elem.posterImagesUrl, 'processedMediaBucket')}
        variant="normal"
        mainHeading={`${elem.chapterNo}. ${elem.chapterTitle}`}
        headingFullWidth={true}
        // blueSubText={`${elem.noOfTopics} Topics`}
        rootStyle={{ width: '100%' }}
        cardClickHandler={async () => {
          if (externalId && isReady) {
            onChapterSelect(navigate, dispatch, externalId, location.pathname);
          }
        }}
        isLocked={!isReady}
      >
        {renderStartResumeButton(
          isReady ? (isStarted ? 'resume' : 'start') : 'error'
        )}
      </ChapterCard>
    );
  });

  const getSubjectName = () => {
    if (typeof subjectId == 'number') {
      return subMap[subjectId]?.subjectName || '';
    } else {
      return t('PAL_LD_INVALID_SUBJECT');
    }
  };

  const getClassDetails = () => {
    if (user?.classSectionDetails?.className) {
      return t('PAL_LD_CLASS_PREFIX', {
        className: user?.classSectionDetails?.className,
        sectionName: user?.classSectionDetails?.sectionName || '',
      });
    }
    return '';
  };

  return (
    <Box sx={styles.root}>
      <Box sx={styles.subjectBox}>
        <SubjectCard
          image={getMediaBasePath(
            subMap[subjectId]?.iconUrl,
            'schoolnetStaticAssetBucket'
          )}
          title={getSubjectName()}
          subTitle={getClassDetails()}
        />
      </Box>
      <Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5vw',
            padding: { xs: pxToRem(20), md: pxTovW(20) },
          }}
        >
          <Typography variant="h2">{t('PAL_LD_ALL_CHAPTERS')}</Typography>
          <Typography variant="h4">
            <ChipBadge
              label={items?.length || 0}
              color="primary"
              size="small"
            />
          </Typography>
        </Box>
        <Box
          sx={{
            marginBottom: { xs: pxToRem(20), md: 0 },
            padding: { xs: 0, md: pxTovW(20) },
          }}
        >
          {loading === 'loading' ? (
            // <Typography variant="h2">Loading ...</Typography>
            <Loader />
          ) : loading === 'error' ? (
            <NoContentCard
              variant="error"
              icon="error"
              text={t('PAL_LD_ERROR_OCCURED')}
            />
          ) : (
            loading === 'completed' &&
            (items ? (
              <DashboardGrid items={items} WebNoOfCols={3} mobileNoOfCols={1} />
            ) : (
              <NoContentCard
                variant="info"
                icon="cards"
                text={t('PAL_LD_NO_CARDS')}
              />
            ))
          )}
        </Box>
      </Box>
      <ActionPopUpSingleButton
        fontSmall={true}
        open={actionPopupState}
        iconName={popupDetails.iconName}
        popupText={popupDetails.popupText}
        handleClose={() => setActionPopupState(false)}
        splitText={popupDetails.splitText}
        ctaHandler={popupDetails.ctaHandler}
        buttontext={t('PAL_LD_POPUP_OK')}
        background="rgba(193, 248, 218, 1)"
      />
    </Box>
  );
}
